/**
 * Managed-user product type used for filter options and active selection state.
 */
import type { IManagerProduct } from '@/types';
import { useCallback } from 'react';

/**
 * Injectable dependency contract for this hook.
 *
 * Dependency injection keeps production wiring simple while enabling isolated
 * unit tests with deterministic assertions.
 */
interface UseManagedUserSharedProductChangeHandlerDependencies {
  /** Error logger abstraction used when selected product cannot be resolved. */
  logError: (...args: unknown[]) => void;
}

/**
 * Runtime defaults used in production.
 */
const defaultDependencies: UseManagedUserSharedProductChangeHandlerDependencies = {
  logError: (...args) => console.error(...args),
};

/**
 * Input params for `useManagedUserSharedProductChangeHandler`.
 */
export interface UseManagedUserSharedProductChangeHandlerParams {
  /** Optional dependency override (primarily for tests). */
  dependencies?: UseManagedUserSharedProductChangeHandlerDependencies;
  /** Available managed-user products used to resolve selected option values. */
  products: IManagerProduct[];
  /** Store action for setting/clearing currently active manager product. */
  setManagerActiveProduct: (product: IManagerProduct | null) => void;
}

export interface UseManagedUserSharedProductChangeHandlerResult {
  /**
   * Handles product filter selection changes emitted by tab table controls.
   *
   * Behavior:
   * 1) `'all'` clears active product
   * 2) Any other value attempts to resolve product by `certificationId`
   * 3) On match, sets active product
   * 4) On miss, logs an error for diagnostics
   */
  handleSharedProductChange: (value: string) => void;
}

/**
 * Encapsulates managed-user product filter selection logic in a standalone,
 * testable hook.
 *
 * This keeps selection/resolution logic outside of the larger feature hook,
 * making it easier to reason about and independently unit test.
 */
export const useManagedUserSharedProductChangeHandler = ({
  dependencies = defaultDependencies,
  products,
  setManagerActiveProduct,
}: UseManagedUserSharedProductChangeHandlerParams): UseManagedUserSharedProductChangeHandlerResult => {
  /**
   * Memoized callback passed to tab UI components.
   *
   * Function identity remains stable unless dependencies actually change.
   */
  const handleSharedProductChange = useCallback(
    (value: string) => {
      // Sentinel option: clear active product and show all data.
      if (value === 'all') return setManagerActiveProduct(null);

      // Resolve selected product by comparing the option value (string)
      // against each product's certification id.
      const selectedProduct = products.find((c) => c?.certificationId.toString() === value);

      // Apply selection when resolved; otherwise emit a diagnostics log.
      if (selectedProduct) setManagerActiveProduct(selectedProduct);
      else dependencies.logError('Selected product not found:', value);
    },
    [dependencies, products, setManagerActiveProduct],
  );

  // Stable hook API
  return {
    handleSharedProductChange,
  };
};
